import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
  captureAttribution,
  trackEvent,
  trackMetaCustomEvent,
  trackMetaEvent,
  trackOutboundBookingEvent,
} from "@/lib/analytics";

const PROPERTY_BY_PATH: Record<string, string> = {
  "/collection/disney": "reunion",
  "/collection/atlantis": "atlantis",
  "/collection/centre-court": "centre-court",
  "/collection/sun-valley": "sun-valley",
};

const PROPERTY_LABEL_BY_SLUG: Record<string, string> = {
  reunion: "Plentiful Views Reunion Resort",
  atlantis: "Plentiful Views Atlantis",
  "centre-court": "Plentiful Views Centre Court",
  "sun-valley": "Plentiful Views Sun Valley",
};

const GUIDE_DESTINATION_BY_PATH: Record<string, string> = {
  "/guides": "all_guides",
  "/guides/where-to-stay-in-san-juan": "san_juan",
  "/guides/san-juan-before-after-cruise": "san_juan",
  "/guides/escambron-beach-san-juan": "san_juan",
  "/guides/disney-resort-hotel-vs-vacation-home": "reunion_orlando",
  "/guides/reunion-resort-for-families": "reunion_orlando",
  "/guides/reunion-resort-water-park-access": "reunion_orlando",
};

const PAID_LANDING_BY_PATH: Record<
  string,
  { variant: string; propertyPath: string; propertyName: string }
> = {
  "/stay/disney-private-pool": {
    variant: "reunion-disney-family",
    propertyPath: "/collection/disney",
    propertyName: "reunion",
  },
  "/stay/san-juan-city-beach": {
    variant: "san-juan-city-beach",
    propertyPath: "/collection/atlantis",
    propertyName: "atlantis",
  },
};

const GUIDE_ASSIST_STORAGE_KEY = "pv_guide_assist";
const GUIDE_ASSIST_TTL_MS = 4 * 60 * 60 * 1000;
const PAID_ASSIST_STORAGE_KEY = "pv_paid_landing_assist";
const PAID_ASSIST_TTL_MS = 4 * 60 * 60 * 1000;

type GuideAssist = {
  guide_path: string;
  guide_title: string;
  guide_destination: string;
  property_path: string;
  captured_at: number;
};

type PaidLandingAssist = {
  landing_path: string;
  landing_variant: string;
  property_path: string;
  property_name: string;
  captured_at: number;
};

const isGuidePath = (pathname: string) => pathname === "/guides" || pathname.startsWith("/guides/");

const inferGuideDestination = (pathname: string) => {
  if (GUIDE_DESTINATION_BY_PATH[pathname]) return GUIDE_DESTINATION_BY_PATH[pathname];
  if (/san-juan|escambron|cruise/i.test(pathname)) return "san_juan";
  if (/reunion|disney/i.test(pathname)) return "reunion_orlando";
  return "other";
};

const inferGuideSlug = (pathname: string) => {
  if (pathname === "/guides") return "guides_hub";
  return pathname.split("/").filter(Boolean).at(-1) || "unknown";
};

const storeGuideAssist = (assist: GuideAssist) => {
  try {
    window.sessionStorage.setItem(GUIDE_ASSIST_STORAGE_KEY, JSON.stringify(assist));
  } catch {
    // Analytics must never interfere with the visitor experience.
  }
};

const readGuideAssist = (): GuideAssist | null => {
  try {
    const stored = window.sessionStorage.getItem(GUIDE_ASSIST_STORAGE_KEY);
    if (!stored) return null;

    const assist = JSON.parse(stored) as GuideAssist;
    if (!assist.captured_at || Date.now() - assist.captured_at > GUIDE_ASSIST_TTL_MS) {
      window.sessionStorage.removeItem(GUIDE_ASSIST_STORAGE_KEY);
      return null;
    }

    return assist;
  } catch {
    return null;
  }
};

const storePaidLandingAssist = (assist: PaidLandingAssist) => {
  try {
    window.sessionStorage.setItem(PAID_ASSIST_STORAGE_KEY, JSON.stringify(assist));
  } catch {
    // Analytics must never interfere with the visitor experience.
  }
};

const readPaidLandingAssist = (): PaidLandingAssist | null => {
  try {
    const stored = window.sessionStorage.getItem(PAID_ASSIST_STORAGE_KEY);
    if (!stored) return null;

    const assist = JSON.parse(stored) as PaidLandingAssist;
    if (!assist.captured_at || Date.now() - assist.captured_at > PAID_ASSIST_TTL_MS) {
      window.sessionStorage.removeItem(PAID_ASSIST_STORAGE_KEY);
      return null;
    }

    return assist;
  } catch {
    return null;
  }
};

const inferPlatform = (href: string) => {
  try {
    const hostname = new URL(href, window.location.origin).hostname.toLowerCase();
    if (hostname.includes("airbnb.")) return "airbnb";
    if (hostname.includes("vrbo.")) return "vrbo";
  } catch {
    return null;
  }

  return null;
};

const inferPropertyName = (href: string, pathname: string) => {
  if (PROPERTY_BY_PATH[pathname]) return PROPERTY_BY_PATH[pathname];

  if (href.includes("31145672") || href.includes("1700932")) return "atlantis";
  if (href.includes("45380595") || href.includes("2095304")) return "reunion";

  return "unknown";
};

const inferSurface = (anchor: HTMLAnchorElement) => {
  if (anchor.dataset.bookingSurface) return anchor.dataset.bookingSurface;

  const section = anchor.closest("section[id]") as HTMLElement | null;
  if (section?.id) return section.id;
  if (anchor.closest("header")) return "header";
  if (anchor.closest("footer")) return "footer";

  return "inline";
};

const SiteAnalytics = () => {
  const location = useLocation();
  const hasMounted = useRef(false);

  useEffect(() => {
    captureAttribution();

    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    const timer = window.setTimeout(() => {
      const pagePath = `${location.pathname}${location.search}`;
      const params = {
        page_path: pagePath,
        page_location: window.location.href,
        page_title: document.title,
      };

      trackEvent("page_view", params);
      trackMetaEvent("PageView", params);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isGuidePath(location.pathname)) return;

    const timer = window.setTimeout(() => {
      const params = {
        guide_path: location.pathname,
        guide_slug: inferGuideSlug(location.pathname),
        guide_destination: inferGuideDestination(location.pathname),
        page_title: document.title,
      };

      trackEvent("guide_view", params);
      trackMetaCustomEvent("GuideView", params);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const paidLanding = PAID_LANDING_BY_PATH[location.pathname];
    if (!paidLanding) return;

    storePaidLandingAssist({
      landing_path: location.pathname,
      landing_variant: paidLanding.variant,
      property_path: paidLanding.propertyPath,
      property_name: paidLanding.propertyName,
      captured_at: Date.now(),
    });
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      const currentPath = window.location.pathname;
      let targetUrl: URL | null = null;

      try {
        targetUrl = new URL(anchor.href, window.location.origin);
      } catch {
        targetUrl = null;
      }

      if (isGuidePath(currentPath) && targetUrl?.origin === window.location.origin) {
        const targetPath = targetUrl.pathname;
        const guideParams = {
          guide_path: currentPath,
          guide_slug: inferGuideSlug(currentPath),
          guide_destination: inferGuideDestination(currentPath),
          link_surface: inferSurface(anchor),
        };

        if (PROPERTY_BY_PATH[targetPath]) {
          const params = {
            ...guideParams,
            property_path: targetPath,
            property_name: PROPERTY_BY_PATH[targetPath],
            property_label: PROPERTY_LABEL_BY_SLUG[PROPERTY_BY_PATH[targetPath]],
          };

          trackEvent("guide_property_click", params);
          trackMetaCustomEvent("GuidePropertyClick", params);
          storeGuideAssist({
            guide_path: currentPath,
            guide_title: document.title,
            guide_destination: inferGuideDestination(currentPath),
            property_path: targetPath,
            captured_at: Date.now(),
          });
        } else if (isGuidePath(targetPath)) {
          trackEvent("guide_internal_click", {
            ...guideParams,
            destination_guide_path: targetPath,
            destination_guide_slug: inferGuideSlug(targetPath),
          });
        }
      }

      const platform = inferPlatform(anchor.href);
      if (!platform) return;

      const pagePath = window.location.pathname;
      const propertyName = inferPropertyName(anchor.href, pagePath);
      const guideAssist = readGuideAssist();
      const matchingGuideAssist =
        guideAssist &&
        (guideAssist.property_path === pagePath || PROPERTY_BY_PATH[guideAssist.property_path] === propertyName)
          ? guideAssist
          : null;

      const currentPaidLanding = PAID_LANDING_BY_PATH[pagePath] || null;
      const paidAssist = readPaidLandingAssist();
      const matchingPaidAssist =
        !currentPaidLanding &&
        paidAssist &&
        (paidAssist.property_path === pagePath || paidAssist.property_name === propertyName)
          ? paidAssist
          : null;

      const params = {
        property_name: propertyName,
        property_label: PROPERTY_LABEL_BY_SLUG[propertyName] || propertyName,
        booking_platform: platform,
        platform,
        destination_url: anchor.href,
        destination_domain: targetUrl?.hostname?.toLowerCase().replace(/^www\./, "") || "",
        click_text: anchor.textContent?.replace(/\s+/g, " ").trim() || "",
        booking_surface: inferSurface(anchor),
        page_path: pagePath,
        ...(currentPaidLanding
          ? {
              paid_landing_variant: currentPaidLanding.variant,
              paid_landing_path: pagePath,
            }
          : {}),
        ...(matchingPaidAssist
          ? {
              paid_assist_variant: matchingPaidAssist.landing_variant,
              paid_assist_path: matchingPaidAssist.landing_path,
              paid_assist_property_path: matchingPaidAssist.property_path,
            }
          : {}),
        ...(matchingGuideAssist
          ? {
              guide_assist_path: matchingGuideAssist.guide_path,
              guide_assist_destination: matchingGuideAssist.guide_destination,
              guide_assist_property_path: matchingGuideAssist.property_path,
            }
          : {}),
      };

      trackOutboundBookingEvent(params);
      trackMetaCustomEvent("OutboundBookingClick", params);

      if (currentPaidLanding) {
        trackEvent("paid_landing_booking_click", params);
        trackMetaCustomEvent("PaidLandingBookingClick", params);
      }

      if (matchingGuideAssist) {
        trackEvent("guide_assisted_booking_click", params);
        trackMetaCustomEvent("GuideAssistedBookingClick", params);
      }

      if (matchingPaidAssist) {
        trackEvent("paid_landing_assisted_booking_click", params);
        trackMetaCustomEvent("PaidLandingAssistedBookingClick", params);
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  return null;
};

export default SiteAnalytics;
